import { List, OrderedMap } from '@gitbook/util-immutable';

import { isInline, MODEL_TYPES } from '../constants';
import { generateKey } from '../utils';
import { Data } from './data';
import type { Fragment, FragmentMap } from './fragment';
import { AbstractNode, memoizeMethods } from './node';
import { Text } from './text';

export interface InlineProperties {
    key: string;
    data: Data;
    nodes: List<Text | Inline>;
    isVoid: boolean;
    type: string;
    fragments: FragmentMap;
}

export type InlineCreateProps =
    | Inline
    | string
    | Partial<
          Omit<InlineProperties, 'data' | 'nodes' | 'fragments'> & {
              nodes: List<Inline | Text> | Array<Inline | Text>;
              data: object | Data;
              fragments: FragmentMap | Fragment[];
          }
      >;

/*
 * Model for an inline node.
 */
export class Inline extends AbstractNode {
    declare public readonly key: string;
    declare public readonly type: string;
    declare public readonly data: Data;
    declare public readonly isVoid: boolean;
    declare public readonly nodes: List<Text | Inline>;
    declare public readonly fragments: FragmentMap;
    declare public readonly fragment: never;

    /*
     * Object.
     */
    get object(): 'inline' {
        return 'inline';
    }

    /*
     * Check if the inline is empty.
     * Returns true if inline is not void and all it's children nodes are empty.
     * Void node is never empty, regardless of it's content.
     */
    get isEmpty(): boolean {
        return !this.isVoid && !this.nodes.some((child) => !child.isEmpty);
    }

    /*
     * Get the concatenated text of all the inline's children.
     */

    get text(): string {
        return this.getText();
    }

    /**
     * Check if `input` is a `Inline`.
     */
    public static isInline(input: any): input is Inline {
        return isInline(input);
    }

    /**
     * Check if `any` is a list of inlines.
     */
    public static isInlineList(input: any): input is List<Inline> {
        return List.isList(input) && input.every((item) => Inline.isInline(item));
    }

    /**
     * Create a new `Inline` with `attrs`.
     */
    public static create(attrs: InlineCreateProps = {}): Inline {
        if (Inline.isInline(attrs)) {
            return attrs;
        }

        if (typeof attrs === 'string') {
            return (Inline.createRecord as (props: object) => Inline)({
                key: generateKey(),
                type: attrs,
            });
        }

        return (Inline.createRecord as (props: object) => Inline)({
            ...attrs,
            key: attrs.key || generateKey(),
            data: Data.create(attrs.data || {}),
            isVoid: attrs.isVoid ?? false,
            nodes: List.create(attrs.nodes || []),
            fragments: OrderedMap.isOrderedMap(attrs.fragments)
                ? attrs.fragments
                : OrderedMap.create(
                      (attrs.fragments || []).map((fragment): [string, Fragment] => [
                          fragment.fragmentKey,
                          fragment,
                      ])
                  ),
        });
    }

    /**
     * Create a new `Inline` void with `attrs`.
     * It already initialize its empty text to avoid unecessary normalization.
     */
    public static createVoid(attrs: Omit<InlineCreateProps, 'isVoid'> = {}): Inline {
        if (Inline.isInline(attrs)) {
            return attrs.merge({
                isVoid: true,
                nodes: List.create([Text.create()]),
            });
        }

        if (typeof attrs === 'string') {
            return (Inline.createRecord as (props: object) => Inline)({
                key: generateKey(),
                type: attrs,
                isVoid: true,
                nodes: List.create([Text.create()]),
            });
        }

        const fragments = (attrs as { fragments?: FragmentMap | Fragment[] }).fragments;
        return Inline.create({
            isVoid: true,
            nodes: List.create([Text.create()]),
            ...attrs,
            fragments: OrderedMap.isOrderedMap(fragments)
                ? fragments
                : OrderedMap.create(
                      (fragments || []).map((fragment): [string, Fragment] => [
                          fragment.fragmentKey,
                          fragment,
                      ])
                  ),
        });
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Inline.prototype[MODEL_TYPES.INLINE] = true;

memoizeMethods(Inline);
